import React from "react";
import PropTypes from "prop-types";

import { fetchInternalFairStatsV2 } from "@/api/events";
import NoDataPreview from "@/components/shared/noDataPreview";

import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
    MenuItem,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";

const useStyles = makeStyles((theme) => ({
    summaryCard: {
        border: "1px solid #d9e3ea",
        borderRadius: theme.spacing(1),
        background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
        padding: theme.spacing(2),
        height: "100%",
    },
    summaryLabel: {
        fontSize: "12px",
        color: "#6b7a88",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: theme.spacing(0.75),
    },
    summaryValue: {
        color: "#183247",
        fontWeight: 600,
    },
    sectionTitle: {
        marginBottom: theme.spacing(1.5),
        color: "#183247",
        fontWeight: 600,
    },
    tableCell: {
        verticalAlign: "top",
        wordBreak: "break-word",
    },
    tableHeadCell: {
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    sectionBlock: {
        marginTop: theme.spacing(3),
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    compactField: {
        minWidth: 220,
        background: theme.palette.common.white,
        [theme.breakpoints.down("sm")]: {
            minWidth: 0,
            width: "100%",
        },
    },
    actionsRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            justifyContent: "stretch",
            "& > button": {
                width: "100%",
            },
        },
    },
}));

const sortOptions = [
    { value: "attended-desc", label: "Attended Count: High to Low" },
    { value: "attended-asc", label: "Attended Count: Low to High" },
    { value: "notes-added-desc", label: "Notes Added: High to Low" },
    { value: "notes-missing-desc", label: "Notes Missing: High to Low" },
    { value: "university-asc", label: "University: A to Z" },
    { value: "university-desc", label: "University: Z to A" },
];

export default function StatsTab({ fairId }) {
    const classes = useStyles();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [statusSearchTerm, setStatusSearchTerm] = React.useState("");
    const [statusSort, setStatusSort] = React.useState("attended-desc");
    const [appliedStatusSearchTerm, setAppliedStatusSearchTerm] = React.useState("");
    const [appliedStatusSort, setAppliedStatusSort] = React.useState("attended-desc");
    const [stats, setStats] = React.useState({
        summary: {
            registeredStudents: 0,
            attendedStudents: 0,
            averageAttendedUniversities: "0.00",
        },
        universityNotesStatusRows: [],
        attendanceRangeRows: [],
    });

    const loadStats = React.useCallback(() => {
        setLoading(true);
        setError("");

        const sortMap = {
            "attended-desc": { sort_by: "attended", sort_order: "desc" },
            "attended-asc": { sort_by: "attended", sort_order: "asc" },
            "notes-added-desc": { sort_by: "notesAdded", sort_order: "desc" },
            "notes-missing-desc": { sort_by: "notesMissing", sort_order: "desc" },
            "university-asc": { sort_by: "university", sort_order: "asc" },
            "university-desc": { sort_by: "university", sort_order: "desc" },
        };

        const selectedSort = sortMap[appliedStatusSort] || sortMap["attended-desc"];

        return fetchInternalFairStatsV2({
            fair_id: fairId,
            university_search: appliedStatusSearchTerm,
            sort_by: selectedSort.sort_by,
            sort_order: selectedSort.sort_order,
        })
            .then((result) => {
                setStats(result.data || {
                    summary: {
                        registeredStudents: 0,
                        attendedStudents: 0,
                        averageAttendedUniversities: "0.00",
                    },
                    universityNotesStatusRows: [],
                    attendanceRangeRows: [],
                });
            })
            .catch(() => {
                setStats({
                    summary: {
                        registeredStudents: 0,
                        attendedStudents: 0,
                        averageAttendedUniversities: "0.00",
                    },
                    universityNotesStatusRows: [],
                    attendanceRangeRows: [],
                });
                setError("Unable to load student stats right now.");
            })
            .finally(() => setLoading(false));
    }, [appliedStatusSearchTerm, appliedStatusSort, fairId]);

    React.useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleApplyFilters = () => {
        setAppliedStatusSearchTerm(statusSearchTerm);
        setAppliedStatusSort(statusSort);
    };

    if (loading) {
        return (
            <Box textAlign="center" py={4}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (!loading && !stats.summary.registeredStudents && !(stats.universityNotesStatusRows || []).length) {
        return <NoDataPreview title={error || "No student stats found"} />;
    }

    return (
        <>
            <Box className={classes.actionsRow}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={loadStats}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                >
                    Refresh Stats
                </Button>
            </Box>

            {error ? (
                <Typography variant="body2" color="error" gutterBottom>
                    {error}
                </Typography>
            ) : null}

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Box className={classes.summaryCard}>
                        <Typography className={classes.summaryLabel}>Registered Students</Typography>
                        <Typography variant="h3" className={classes.summaryValue}>
                            {stats.summary.registeredStudents}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box className={classes.summaryCard}>
                        <Typography className={classes.summaryLabel}>Attended Students</Typography>
                        <Typography variant="h3" className={classes.summaryValue}>
                            {stats.summary.attendedStudents}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box className={classes.summaryCard}>
                        <Typography className={classes.summaryLabel}>Average Universities Attended</Typography>
                        <Typography variant="h3" className={classes.summaryValue}>
                            {stats.summary.averageAttendedUniversities}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            <Box className={classes.sectionBlock}>
                <Box className={classes.sectionHeader}>
                    <Typography variant="h3" className={classes.sectionTitle}>
                        University-wise Attended vs Notes Status
                    </Typography>
                    <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item xs={12} md={4}>
                            <TextField
                                className={classes.compactField}
                                variant="outlined"
                                label="Filter Universities"
                                value={statusSearchTerm}
                                onChange={(event) => setStatusSearchTerm(event.target.value)}
                                placeholder="Search by university name"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                className={classes.compactField}
                                select
                                variant="outlined"
                                label="Sort By"
                                value={statusSort}
                                onChange={(event) => setStatusSort(event.target.value)}
                            >
                                {sortOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button variant="contained" color="primary" onClick={handleApplyFilters}>
                                Apply
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {(stats.universityNotesStatusRows || []).length === 0 ? (
                    <NoDataPreview title="No university note-status rows found" />
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>University</TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Attended Count</TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Notes Added</TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Notes Missing</TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Coverage</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(stats.universityNotesStatusRows || []).map((row) => (
                                    <TableRow key={row.universityName}>
                                        <TableCell className={classes.tableCell}>{row.universityName}</TableCell>
                                        <TableCell className={classes.tableCell}>{row.attendedCount}</TableCell>
                                        <TableCell className={classes.tableCell}>{row.notesAddedCount}</TableCell>
                                        <TableCell className={classes.tableCell}>{row.notesMissingCount}</TableCell>
                                        <TableCell className={classes.tableCell}>{row.notesCoverage}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            <Box className={classes.sectionBlock}>
                <Typography variant="h3" className={classes.sectionTitle}>
                    University Attendance Range
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Range</TableCell>
                                <TableCell className={`${classes.tableCell} ${classes.tableHeadCell}`}>Student Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(stats.attendanceRangeRows || []).map((row) => (
                                <TableRow key={row.label}>
                                    <TableCell className={classes.tableCell}>{row.label}</TableCell>
                                    <TableCell className={classes.tableCell}>{row.studentCount}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
}

StatsTab.propTypes = {
    fairId: PropTypes.string.isRequired,
};
